import type { Cell, CoverRequest, CoverResult } from "./types";
import { buildCoverageLP } from "./coverageModel";
import { solveLp } from "./highsWorker";

/**
 * Solve a Maximal Covering Location instance with HiGHS, returning the chosen
 * facility cells and the demand points they cover. Runs in the shared Web
 * Worker so the host game's UI never freezes.
 */
export async function solveCoverage(req: CoverRequest): Promise<CoverResult> {
  const { timeLimitSec = 30 } = req;

  const t0 = performance.now();
  const { lp, candidates, demands, coverage } = buildCoverageLP(req);
  const tBuildMs = performance.now() - t0;

  // tSolveMs is measured inside the worker around highs.solve() only, so it
  // never includes the one-time WASM init or the wait behind queued solves.
  const { sol, solveMs: tSolveMs } = await solveLp(lp, { time_limit: timeLimitSec, presolve: "on" });

  const chosen: Cell[] = [];
  if (sol.Status === "Optimal" || sol.Status === "Feasible") {
    for (let j = 0; j < candidates.length; j++) {
      const col = sol.Columns["y" + j];
      if (col && col.Primal > 0.5) chosen.push(candidates[j]);
    }
  }

  // Derive covered demands from the chosen sites + the coverage structure so the
  // returned set is exactly consistent with the facilities (not the z columns,
  // which can sit at a fractional-but-rounded value the UI shouldn't trust).
  const chosenSet = new Set(chosen.map(([r, c]) => r + "," + c));
  const candIndex = new Set<number>();
  for (let j = 0; j < candidates.length; j++) {
    if (chosenSet.has(candidates[j][0] + "," + candidates[j][1])) candIndex.add(j);
  }
  const coveredDemands: Cell[] = [];
  for (let i = 0; i < demands.length; i++) {
    if (coverage[i].some((j) => candIndex.has(j))) coveredDemands.push(demands[i]);
  }

  const status =
    sol.Status === "Optimal" ? "OPTIMAL" :
    sol.Status === "Feasible" ? "FEASIBLE" :
    String(sol.Status).toUpperCase();

  return {
    status,
    covered: coveredDemands.length,
    total: demands.length,
    chosen,
    coveredDemands,
    tBuildMs,
    tSolveMs,
  };
}
